import { appBase } from './publicPath';
import { startGlobalLoading, stopGlobalLoading, withGlobalLoader } from './appLoading';
import { Capacitor } from '@capacitor/core';

const REQUEST_TIMEOUT_MS = 7000;
const FALLBACK_SITE_ORIGIN = 'https://servicesanddeveloper.in';

function trimTrailingSlash(value = '') {
  return String(value).replace(/\/+$/, '');
}

function resolveApiBase(explicitBase, relativePath) {
  const isBrowserLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  if (isBrowserLocalhost) {
    const liveOrigin = trimTrailingSlash(
      process.env.REACT_APP_SITE_ORIGIN
      || process.env.REACT_APP_MOBILE_SITE_ORIGIN
      || ''
    );
    return liveOrigin ? `${liveOrigin}${relativePath}` : `${appBase}${relativePath}`;
  }

  if (Capacitor.isNativePlatform()) {
    const nativeOrigin = trimTrailingSlash(
      process.env.REACT_APP_MOBILE_SITE_ORIGIN
      || process.env.REACT_APP_SITE_ORIGIN
      || FALLBACK_SITE_ORIGIN
    );
    return `${nativeOrigin}${relativePath}`;
  }

  if (explicitBase) {
    return trimTrailingSlash(explicitBase);
  }

  return `${appBase}${relativePath}`;
}

const API_BASE = resolveApiBase(process.env.REACT_APP_AUTH_API_BASE, '/api/auth');
const CMS_API_BASE = resolveApiBase(process.env.REACT_APP_CMS_API_BASE, '/api/cms');

let csrfTokenCache = null;

const fetchWithTimeout = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('Request timed out. The server is taking too long to respond. Please try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    const message = data?.message || 'Request failed. Please try again.';
    const err = new Error(message);
    err.code = data?.code || null;
    throw err;
  }
  return data;
};

const getCsrfToken = async () => {
  if (csrfTokenCache) return csrfTokenCache;

  const response = await withGlobalLoader(() => fetchWithTimeout(`${API_BASE}/csrf.php`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }));
  const data = await parseResponse(response);
  csrfTokenCache = data?.csrfToken || null;
  return csrfTokenCache;
};

const requestJson = async (path, method = 'GET', payload = null) => {
  const headers = {
    Accept: 'application/json',
  };

  if (payload != null) {
    headers['Content-Type'] = 'application/json';
  }

  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrf = await getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const response = await withGlobalLoader(() => fetchWithTimeout(`${API_BASE}/${path}`, {
    method,
    credentials: 'include',
    headers,
    body: payload == null ? undefined : JSON.stringify(payload),
  }));

  return parseResponse(response);
};

const postJson = async (path, payload) => requestJson(path, 'POST', payload);
const getJson = async (path) => requestJson(path, 'GET');
// eslint-disable-next-line no-unused-vars
const deleteJson = async (path, payload = {}) => requestJson(path, 'DELETE', payload);

const requestCmsJson = async (path, method = 'GET', payload = null) => {
  const headers = {
    Accept: 'application/json',
  };

  if (payload != null) {
    headers['Content-Type'] = 'application/json';
  }

  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrf = await getCsrfToken().catch(() => null);
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  try {
    const response = await fetchWithTimeout(`${CMS_API_BASE}/${path}`, {
      method,
      credentials: method === 'GET' ? 'same-origin' : 'include',
      headers,
      body: payload == null ? undefined : JSON.stringify(payload),
    });

    if (response.status === 404) {
      return { success: false, menus: [], settings: {}, data: [] };
    }

    return await parseResponse(response);
  } catch (err) {
    if (method === 'GET') {
      return { success: false, menus: [], settings: {}, data: [] };
    }
    throw err;
  }
};

const getCmsJson = async (path) => requestCmsJson(path, 'GET');
const postCmsJson = async (path, payload) => requestCmsJson(path, 'POST', payload);

export const signupWithSqlAuth = async ({ name, email, password }) => {
  const res = await postJson('register.php', { name, email, password });
  csrfTokenCache = null;
  return res;
};

export const loginWithSqlAuth = async ({ email, password }) => {
  const res = await postJson('login.php', { email, password });
  csrfTokenCache = null;
  return res;
};

export const getCurrentUser = async () => {
  const data = await getJson('me.php');
  csrfTokenCache = data?.csrfToken || csrfTokenCache;
  return data;
};

export const getDynamicPage = async ({ page = 'home', admin = false } = {}) =>
  getCmsJson(`sections.php?page=${encodeURIComponent(page)}${admin ? '&admin=1' : ''}`);

export const createDynamicSection = async (payload) =>
  postCmsJson('sections.php', { action: 'create', ...payload });

export const updateDynamicSection = async (payload) =>
  postCmsJson('sections.php', { action: 'update', ...payload });

export const deleteDynamicSection = async ({ id }) =>
  postCmsJson('sections.php', { action: 'delete', id });

export const toggleDynamicSection = async ({ id, isEnabled }) =>
  postCmsJson('sections.php', { action: 'toggle', id, isEnabled });

export const reorderDynamicSections = async ({ orderedIds }) =>
  postCmsJson('sections.php', { action: 'reorder', orderedIds });

export const getTestimonials = async ({ admin = false } = {}) =>
  getCmsJson(`testimonials.php${admin ? '?admin=1' : ''}`);

export const createTestimonial = async (payload) =>
  postCmsJson('testimonials.php', { action: 'create', ...payload });

export const updateTestimonial = async (payload) =>
  postCmsJson('testimonials.php', { action: 'update', ...payload });

export const deleteTestimonial = async ({ id }) =>
  postCmsJson('testimonials.php', { action: 'delete', id });

export const toggleTestimonial = async ({ id, isEnabled }) =>
  postCmsJson('testimonials.php', { action: 'toggle', id, isEnabled });

export const reorderTestimonials = async ({ orderedIds }) =>
  postCmsJson('testimonials.php', { action: 'reorder', orderedIds });

export const getCmsMediaLibrary = async () => getCmsJson('media.php');

export const uploadCmsMedia = async ({ file, altText = '' }) => {
  const csrf = await getCsrfToken();
  const formData = new FormData();
  formData.append('file', file);
  if (altText) formData.append('altText', altText);

  const response = await withGlobalLoader(() => fetchWithTimeout(`${CMS_API_BASE}/media.php`, {
    method: 'POST',
    credentials: 'include',
    headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
    body: formData,
  }));
  return parseResponse(response);
};

export const deleteCmsMedia = async ({ id }) =>
  postCmsJson('media.php', { action: 'delete', id });

export const getCmsSettings = async ({ keys = [] } = {}) => {
  if (!Array.isArray(keys) || keys.length === 0) return getCmsJson('settings.php');
  const query = keys.map((key) => `keys[]=${encodeURIComponent(key)}`).join('&');
  return getCmsJson(`settings.php?${query}`);
};

export const saveCmsSettings = async ({ items }) =>
  postCmsJson('settings.php', { items });

const createCmsEntityHelpers = (name) => ({
  get: ({ admin = false } = {}) => getCmsJson(`${name}.php${admin ? '?admin=1' : ''}`),
  create: (payload) => postCmsJson(`${name}.php`, { action: 'create', ...payload }),
  update: (payload) => postCmsJson(`${name}.php`, { action: 'update', ...payload }),
  remove: ({ id }) => postCmsJson(`${name}.php`, { action: 'delete', id }),
  toggle: ({ id, isEnabled }) => postCmsJson(`${name}.php`, { action: 'toggle', id, isEnabled }),
  reorder: ({ orderedIds }) => postCmsJson(`${name}.php`, { action: 'reorder', orderedIds }),
});

const cmsServices = createCmsEntityHelpers('services');
const cmsProducts = createCmsEntityHelpers('products');
const cmsTeam = createCmsEntityHelpers('team');
const cmsJobs = createCmsEntityHelpers('career_jobs');

export const getCmsServices = cmsServices.get;
export const createCmsService = cmsServices.create;
export const updateCmsService = cmsServices.update;
export const deleteCmsService = cmsServices.remove;
export const toggleCmsService = cmsServices.toggle;
export const reorderCmsServices = cmsServices.reorder;

export const getCmsProducts = cmsProducts.get;
export const createCmsProduct = cmsProducts.create;
export const updateCmsProduct = cmsProducts.update;
export const deleteCmsProduct = cmsProducts.remove;
export const toggleCmsProduct = cmsProducts.toggle;
export const reorderCmsProducts = cmsProducts.reorder;

export const getCmsTeam = cmsTeam.get;
export const createCmsTeamMember = cmsTeam.create;
export const updateCmsTeamMember = cmsTeam.update;
export const deleteCmsTeamMember = cmsTeam.remove;
export const toggleCmsTeamMember = cmsTeam.toggle;
export const reorderCmsTeam = cmsTeam.reorder;

export const getCmsCareerJobs = cmsJobs.get;
export const createCmsCareerJob = cmsJobs.create;
export const updateCmsCareerJob = cmsJobs.update;
export const deleteCmsCareerJob = cmsJobs.remove;
export const toggleCmsCareerJob = cmsJobs.toggle;
export const reorderCmsCareerJobs = cmsJobs.reorder;

export const getHeaderMenu = async () => getCmsJson('menus.php?location=header');
export const getFooterMenu = async () => getCmsJson('menus.php?location=footer');
export const getCmsMenus = async ({ location = '', admin = false } = {}) =>
  getCmsJson(`menus.php${location || admin ? '?' : ''}${location ? `location=${encodeURIComponent(location)}` : ''}${location && admin ? '&' : ''}${admin ? 'admin=1' : ''}`);
export const upsertCmsMenu = async (payload) =>
  postCmsJson('menus.php', { action: 'upsert_menu', ...payload });
export const createCmsMenuItem = async (payload) =>
  postCmsJson('menus.php', { action: 'create_item', ...payload });
export const updateCmsMenuItem = async (payload) =>
  postCmsJson('menus.php', { action: 'update_item', ...payload });
export const deleteCmsMenuItem = async ({ id }) =>
  postCmsJson('menus.php', { action: 'delete_item', id });
export const reorderCmsMenuItems = async ({ orderedIds }) =>
  postCmsJson('menus.php', { action: 'reorder_items', orderedIds });
export const getSiteSettings = async ({ keys = [] } = {}) => {
  if (!Array.isArray(keys) || keys.length === 0) return getJson('cms_settings.php');
  const query = keys.map((k) => `keys[]=${encodeURIComponent(k)}`).join('&');
  return getJson(`cms_settings.php?${query}`);
};

export const getCmsContent = async ({ key = '' } = {}) =>
  getJson(`cms.php${key ? `?key=${encodeURIComponent(key)}` : ''}`);

export const getPageBySlug = async ({ slug, admin = false, view = 'active' } = {}) =>
  getJson(`cms_pages.php?slug=${encodeURIComponent(slug)}${admin ? '&admin=1' : ''}${admin ? `&view=${encodeURIComponent(view)}` : ''}`);

export const getPages = async ({ admin = false, view = 'active' } = {}) =>
  getJson(`cms_pages.php${admin ? `?admin=1&view=${encodeURIComponent(view)}` : ''}`);

export const createPage = async (payload) =>
  postJson('cms_pages.php', { action: 'create', ...payload });

export const updatePage = async (payload) =>
  postJson('cms_pages.php', { action: 'update', ...payload });

export const setPageStatus = async ({ id, status }) =>
  postJson('cms_pages.php', { action: 'set_status', id, status });

export const deletePage = async ({ id }) =>
  postJson('cms_pages.php', { action: 'delete', id });

export const restorePage = async ({ id }) =>
  postJson('cms_pages.php', { action: 'restore', id });

export const purgePage = async ({ id }) =>
  postJson('cms_pages.php', { action: 'purge', id });

export const getSectionsByPage = async ({ pageId, slug, admin = false, view = 'active' }) => {
  const params = [];
  if (pageId) params.push(`pageId=${encodeURIComponent(String(pageId))}`);
  if (slug) params.push(`slug=${encodeURIComponent(slug)}`);
  if (admin) params.push('admin=1');
  if (admin) params.push(`view=${encodeURIComponent(view)}`);
  return getJson(`cms_sections.php?${params.join('&')}`);
};

export const createSection = async (payload) =>
  postJson('cms_sections.php', { action: 'create', ...payload });

export const updateSection = async (payload) =>
  postJson('cms_sections.php', { action: 'update', ...payload });

export const deleteSection = async ({ id }) =>
  postJson('cms_sections.php', { action: 'delete', id });

export const restoreSection = async ({ id }) =>
  postJson('cms_sections.php', { action: 'restore', id });

export const purgeSection = async ({ id }) =>
  postJson('cms_sections.php', { action: 'purge', id });

export const reorderSections = async ({ orderedIds }) =>
  postJson('cms_sections.php', { action: 'reorder', orderedIds });

export const getMenus = async ({ location = '', admin = false, view = 'active' } = {}) => {
  const params = [];
  if (location) params.push(`location=${encodeURIComponent(location)}`);
  if (admin) params.push('admin=1');
  if (admin) params.push(`view=${encodeURIComponent(view)}`);
  return getJson(`cms_menus.php${params.length ? `?${params.join('&')}` : ''}`);
};

export const createMenu = async (payload) =>
  postJson('cms_menus.php', { action: 'create_menu', ...payload });

export const updateMenu = async (payload) =>
  postJson('cms_menus.php', { action: 'update_menu', ...payload });

export const deleteMenu = async ({ id }) =>
  postJson('cms_menus.php', { action: 'delete_menu', id });

export const restoreMenu = async ({ id }) =>
  postJson('cms_menus.php', { action: 'restore_menu', id });

export const purgeMenu = async ({ id }) =>
  postJson('cms_menus.php', { action: 'purge_menu', id });

export const createMenuItem = async (payload) =>
  postJson('cms_menus.php', { action: 'create_item', ...payload });

export const updateMenuItem = async (payload) =>
  postJson('cms_menus.php', { action: 'update_item', ...payload });

export const reorderMenuItems = async ({ orderedIds }) =>
  postJson('cms_menus.php', { action: 'reorder_items', orderedIds });

export const deleteMenuItem = async ({ id }) =>
  postJson('cms_menus.php', { action: 'delete_item', id });

export const restoreMenuItem = async ({ id }) =>
  postJson('cms_menus.php', { action: 'restore_item', id });

export const purgeMenuItem = async ({ id }) =>
  postJson('cms_menus.php', { action: 'purge_item', id });

export const updateFooterSettings = async (payload) =>
  postJson('cms_menus.php', { action: 'upsert_footer', ...payload });

export const getMediaLibrary = async ({ view = 'active' } = {}) => getJson(`cms_media.php?view=${encodeURIComponent(view)}`);

export const uploadMedia = async ({ file, altText = '' }) => {
  const csrf = await getCsrfToken();
  const formData = new FormData();
  formData.append('file', file);
  if (altText) formData.append('altText', altText);

  const response = await withGlobalLoader(() => fetch(`${API_BASE}/cms_media.php`, {
    method: 'POST',
    credentials: 'include',
    headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
    body: formData,
  }));
  return parseResponse(response);
};

export const deleteMedia = async ({ id }) =>
  postJson('cms_media.php', { action: 'delete', id });

export const restoreMedia = async ({ id }) =>
  postJson('cms_media.php', { action: 'restore', id });

export const purgeMedia = async ({ id }) =>
  postJson('cms_media.php', { action: 'purge', id });

export const updateMedia = async ({ id, altText = '' }) =>
  postJson('cms_media.php', { action: 'update', id, altText });

export const getPosts = async ({ admin = false, view = 'active' } = {}) =>
  getJson(`cms_posts.php${admin ? `?admin=1&view=${encodeURIComponent(view)}` : ''}`);

export const getPostBySlug = async ({ slug, admin = false, view = 'active' } = {}) =>
  getJson(`cms_posts.php?slug=${encodeURIComponent(slug)}${admin ? '&admin=1' : ''}${admin ? `&view=${encodeURIComponent(view)}` : ''}`);

export const createPost = async (payload) =>
  postJson('cms_posts.php', { action: 'create', ...payload });

export const updatePost = async (payload) =>
  postJson('cms_posts.php', { action: 'update', ...payload });

export const deletePost = async ({ id }) =>
  postJson('cms_posts.php', { action: 'delete', id });

export const restorePost = async ({ id }) =>
  postJson('cms_posts.php', { action: 'restore', id });

export const purgePost = async ({ id }) =>
  postJson('cms_posts.php', { action: 'purge', id });

export const getCategories = async ({ admin = false, view = 'active' } = {}) =>
  getJson(`cms_categories.php${admin ? `?admin=1&view=${encodeURIComponent(view)}` : ''}`);

export const createCategory = async (payload) =>
  postJson('cms_categories.php', { action: 'create', ...payload });

export const updateCategory = async (payload) =>
  postJson('cms_categories.php', { action: 'update', ...payload });

export const deleteCategory = async ({ id }) =>
  postJson('cms_categories.php', { action: 'delete', id });

export const restoreCategory = async ({ id }) =>
  postJson('cms_categories.php', { action: 'restore', id });

export const purgeCategory = async ({ id }) =>
  postJson('cms_categories.php', { action: 'purge', id });

export const getTags = async ({ admin = false, view = 'active' } = {}) =>
  getJson(`cms_tags.php${admin ? `?admin=1&view=${encodeURIComponent(view)}` : ''}`);

export const createTag = async (payload) =>
  postJson('cms_tags.php', { action: 'create', ...payload });

export const updateTag = async (payload) =>
  postJson('cms_tags.php', { action: 'update', ...payload });

export const deleteTag = async ({ id }) =>
  postJson('cms_tags.php', { action: 'delete', id });

export const restoreTag = async ({ id }) =>
  postJson('cms_tags.php', { action: 'restore', id });

export const purgeTag = async ({ id }) =>
  postJson('cms_tags.php', { action: 'purge', id });

export const resendVerificationEmail = async ({ email }) =>
  postJson('resend_verification.php', { email });

export const verifyEmailToken = async ({ token }) =>
  postJson('verify_email.php', { token });

export const requestPasswordReset = async ({ email }) =>
  postJson('forgot_password.php', { email });

export const resetPasswordWithToken = async ({ token, password }) =>
  postJson('reset_password.php', { token, password });

export const getProfile = async () => getJson('profile.php');

export const updateProfile = async ({ name, email, phone, avatarUrl }) =>
  postJson('profile.php', { name, email, phone, avatarUrl });

export const uploadProfileAvatar = async ({ file, onProgress }) => {
  const csrf = await getCsrfToken();
  const formData = new FormData();
  formData.append('avatar', file);

  return new Promise((resolve, reject) => {
    startGlobalLoading();
    const request = new XMLHttpRequest();
    request.open('POST', `${API_BASE}/profile_avatar.php`, true);
    request.withCredentials = true;
    request.responseType = 'json';
    if (csrf) {
      request.setRequestHeader('X-CSRF-Token', csrf);
    }

    request.upload.onprogress = (event) => {
      if (typeof onProgress === 'function' && event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    request.onerror = () => {
      stopGlobalLoading();
      reject(new Error('Unable to upload your profile picture right now.'));
    };

    request.onload = () => {
      const data = request.response || JSON.parse(request.responseText || '{}');
      stopGlobalLoading();
      if (request.status < 200 || request.status >= 300 || data?.success === false) {
        reject(new Error(data?.message || 'Unable to upload your profile picture right now.'));
        return;
      }
      resolve(data);
    };

    request.onabort = () => {
      stopGlobalLoading();
      reject(new Error('Upload was interrupted. Please try again.'));
    };

    request.send(formData);
  });
};

export const changePassword = async ({ currentPassword, newPassword }) =>
  postJson('change_password.php', { currentPassword, newPassword });

/* ==========================================================================
   LOCAL CART & ORDERS STORE (Resilient offline-first fallback)
   ========================================================================== */
const LOCAL_CART_KEY = 'sd_local_cart';
const LOCAL_ORDERS_KEY = 'sd_local_orders';

export const getLocalCart = () => {
  if (typeof window === 'undefined') return { items: [], totalInr: 0 };
  try {
    const raw = localStorage.getItem(LOCAL_CART_KEY);
    if (!raw) return { items: [], totalInr: 0 };
    const parsed = JSON.parse(raw);
    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    const totalInr = items.reduce((sum, item) => sum + (Number(item.priceInr) || 0) * (Number(item.quantity) || 1), 0);
    return { items, totalInr };
  } catch (_) {
    return { items: [], totalInr: 0 };
  }
};

export const saveLocalCart = (cart) => {
  if (typeof window === 'undefined') return cart;
  try {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const totalInr = items.reduce((sum, item) => sum + (Number(item.priceInr) || 0) * (Number(item.quantity) || 1), 0);
    const nextCart = { items, totalInr };
    localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(nextCart));
    window.dispatchEvent(new CustomEvent('sd:cart-updated', { detail: nextCart }));
    return nextCart;
  } catch (_) {
    return cart;
  }
};

export const getLocalOrders = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LOCAL_ORDERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
};

export const saveLocalOrders = (orders) => {
  if (typeof window === 'undefined') return orders;
  try {
    localStorage.setItem(LOCAL_ORDERS_KEY, JSON.stringify(orders));
    return orders;
  } catch (_) {
    return orders;
  }
};

export const getCart = async () => {
  // Remote cart.php disabled to prevent net::ERR_FAILED if endpoint is not present on server
  // try {
  //   const res = await getJson('cart.php');
  //   if (res && Array.isArray(res.items) && res.items.length > 0) {
  //     return res;
  //   }
  // } catch (_) {}
  return getLocalCart();
};

export const saveCartItem = async ({ packageId, packageName, priceInr, quantity = 1 }) => {
  const numericPrice = Number(priceInr) || 0;
  const numericQty = Number(quantity) || 1;

  // Update local cart immediately so the UI never blocks
  const current = getLocalCart();
  const existingIndex = current.items.findIndex(
    (item) => String(item.packageId) === String(packageId) || item.packageName === packageName
  );

  let nextItems;
  if (existingIndex >= 0) {
    nextItems = current.items.map((item, idx) =>
      idx === existingIndex
        ? { ...item, quantity: (Number(item.quantity) || 1) + numericQty }
        : item
    );
  } else {
    nextItems = [
      ...current.items,
      {
        id: `cart_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        packageId: packageId || `pkg_${Date.now()}`,
        packageName: packageName || 'Service Package',
        priceInr: numericPrice,
        quantity: numericQty,
      },
    ];
  }
  const updated = saveLocalCart({ items: nextItems });

  // Remote cart sync commented out (cart.php endpoint missing on backend)
  // try {
  //   await postJson('cart.php', { packageId, packageName, priceInr: numericPrice, quantity: numericQty });
  // } catch (_) {}

  return updated;
};

export const removeCartItem = async ({ itemId }) => {
  const current = getLocalCart();
  const nextItems = current.items.filter((item) => item.id !== itemId && item.packageId !== itemId);
  const updated = saveLocalCart({ items: nextItems });

  // Remote cart sync commented out (cart.php endpoint missing on backend)
  // try {
  //   await deleteJson('cart.php', { itemId });
  // } catch (_) {}

  return updated;
};

export const clearCart = async () => {
  saveLocalCart({ items: [], totalInr: 0 });
  // Remote cart sync commented out (cart.php endpoint missing on backend)
  // try {
  //   await deleteJson('cart.php', {});
  // } catch (_) {}
  return { ok: true };
};

export const placeOrder = async ({ paymentMethod = 'cod', packageItem, address, customerNotes } = {}) => {
  const currentCart = getLocalCart();
  const items = packageItem
    ? [
        {
          id: `item_${Date.now()}`,
          packageId: packageItem.packageId,
          packageName: packageItem.packageName,
          priceInr: Number(packageItem.priceInr) || 0,
          quantity: Number(packageItem.quantity) || 1,
        },
      ]
    : (currentCart.items.length ? currentCart.items : [
        {
          id: `item_${Date.now()}`,
          packageId: 'std_package',
          packageName: 'Standard Development Package',
          priceInr: 12999,
          quantity: 1,
        }
      ]);

  const totalInr = items.reduce((sum, item) => sum + (Number(item.priceInr) || 0) * (Number(item.quantity) || 1), 0);
  const orderCode = `ORD-${Math.floor(100000 + Math.random() * 900000)}`;

  const newOrder = {
    id: `order_${Date.now()}`,
    orderCode,
    status: 'in_progress',
    paymentMethod: paymentMethod || 'cod',
    totalInr,
    items,
    address: address || null,
    notes: customerNotes || '',
    createdAt: new Date().toISOString(),
    tracking: [
      {
        status: 'confirmed',
        note: paymentMethod === 'cod' ? 'Order placed with Cash on Delivery (COD).' : 'Order confirmed and payment verified.',
        createdAt: new Date().toISOString(),
      },
      {
        status: 'in_progress',
        note: 'Project assigned to development team. Kickoff started.',
        createdAt: new Date().toISOString(),
      },
    ],
  };

  const currentOrders = getLocalOrders();
  saveLocalOrders([newOrder, ...currentOrders]);

  clearCart();

  try {
    await postJson('order_place.php', {
      paymentMethod,
      orderCode,
      package: packageItem,
      address,
    });
  } catch (_) {}

  return { ok: true, order: newOrder };
};

export const getOrders = async () => {
  const local = getLocalOrders();
  try {
    const res = await getJson('orders.php');
    if (res && Array.isArray(res.orders) && res.orders.length > 0) {
      const map = new Map();
      res.orders.forEach((o) => map.set(o.orderCode || o.id, o));
      local.forEach((o) => {
        if (!map.has(o.orderCode || o.id)) {
          map.set(o.orderCode || o.id, o);
        }
      });
      return { orders: Array.from(map.values()) };
    }
  } catch (_) {}
  return { orders: local };
};

export const getAccountDashboard = async () => getJson('account_dashboard.php').catch(() => null);

export const createSupportTicket = async ({ subject, category, priority, message }) =>
  postJson('account_dashboard.php', {
    action: 'create_ticket',
    subject,
    category,
    priority,
    message,
  });

export const replyToSupportTicket = async ({ ticketId, message }) =>
  postJson('account_dashboard.php', {
    action: 'reply_ticket',
    ticketId,
    message,
  });

export const updateAccountSettings = async (payload) =>
  postJson('account_dashboard.php', {
    action: 'update_settings',
    ...payload,
  });

export const updateAccountSecurity = async (payload) =>
  postJson('account_dashboard.php', {
    action: 'update_security',
    ...payload,
  });

export const markNotificationsRead = async ({ notificationIds = [] } = {}) =>
  postJson('account_dashboard.php', {
    action: 'mark_notifications_read',
    notificationIds,
  });

export const revokeAccountDevice = async ({ deviceId }) =>
  postJson('account_dashboard.php', {
    action: 'revoke_device',
    deviceId,
  });

export const adminGetUsers = async ({ search = '' } = {}) =>
  getJson(`admin_users.php${search ? `?search=${encodeURIComponent(search)}` : ''}`);

export const adminGetUserOrders = async ({ userId }) =>
  getJson(`admin_user_orders.php?userId=${encodeURIComponent(String(userId || ''))}`);

export const adminSendUserNotification = async ({ userId, title, message, linkUrl = '/dashboard/notifications' }) =>
  postJson('admin_user_orders.php', {
    action: 'send_notification',
    userId,
    title,
    message,
    linkUrl,
  });

export const adminUpdateUserRole = async (payload) =>
  postJson('admin_user_update.php', payload);

export const adminCreateUser = async (payload) =>
  postJson('admin_user_update.php', { action: 'create', ...payload });

export const adminGetOrders = async ({ search = '' } = {}) =>
  getJson(`admin_orders.php${search ? `?search=${encodeURIComponent(search)}` : ''}`);

export const adminUpdateOrder = async ({ orderId, status, trackingNote }) =>
  postJson('admin_orders.php', { orderId, status, trackingNote });

export const adminGetNotifications = async () => getJson('admin_notifications.php');

export const adminMarkNotificationsRead = async ({ notificationIds = [] } = {}) =>
  postJson('admin_notifications.php', {
    action: 'mark_read',
    notificationIds,
  });

export const adminGetDashboardView = async ({ view, search = '', dateFrom = '', dateTo = '' } = {}) =>
  getJson(`admin_dashboard.php?view=${encodeURIComponent(view || 'overview')}${search ? `&search=${encodeURIComponent(search)}` : ''}${dateFrom ? `&dateFrom=${encodeURIComponent(dateFrom)}` : ''}${dateTo ? `&dateTo=${encodeURIComponent(dateTo)}` : ''}`);

export const adminDashboardAction = async (payload) =>
  postJson('admin_dashboard.php', payload);

export const logoutWithSqlAuth = async () => {
  const res = await postJson('logout.php', {});
  csrfTokenCache = null;
  return res;
};

// Legacy endpoints retained for compatibility with existing admin dashboard widgets.
export const adminGetMenuItems = async () => getJson('admin_menu.php');
export const adminCreateMenuItem = async ({ title, path, visibility = 'public', isEnabled = true, menuKey = '' }) =>
  requestJson('admin_menu.php', 'POST', {
    action: 'create',
    title,
    path,
    visibility,
    isEnabled,
    menuKey,
  });

export const adminUpdateMenuItem = async ({ id, title, path, visibility, isEnabled }) =>
  requestJson('admin_menu.php', 'POST', {
    action: 'update',
    id,
    title,
    path,
    visibility,
    isEnabled,
  });

export const adminDeleteMenuItem = async ({ id }) =>
  requestJson('admin_menu.php', 'POST', {
    action: 'delete',
    id,
  });

export const adminReorderMenuItems = async ({ orderedIds }) =>
  requestJson('admin_menu.php', 'POST', {
    action: 'reorder',
    orderedIds,
  });

export const adminGetCmsItems = async () => getJson('admin_cms.php');

export const adminCreateCmsItem = async ({ contentKey, title, contentJson, isEnabled = true }) =>
  requestJson('admin_cms.php', 'POST', {
    action: 'create',
    contentKey,
    title,
    contentJson,
    isEnabled,
  });

export const adminUpdateCmsItem = async ({ id, title, contentJson, isEnabled }) =>
  requestJson('admin_cms.php', 'POST', {
    action: 'update',
    id,
    title,
    contentJson,
    isEnabled,
  });

export const adminDeleteCmsItem = async ({ id }) =>
  requestJson('admin_cms.php', 'POST', {
    action: 'delete',
    id,
  });
